export const POS_RECEIPT_EMAIL_COLLECTION = "posReceiptEmailTemplates";
export const POS_RECEIPT_EMAIL_DOC_ID = "default";

export interface PosReceiptEmailTemplate {
  logoUrl: string;
  brandColor: string;
  headingEn: string;
  headingFr: string;
  footerEn: string;
  footerFr: string;
  subjectFormat: string;
  htmlShell: string;
}

// Placeholders: {{lang}}, {{brandColor}}, {{logoUrl}}, {{heading}}, {{storeName}},
// {{receiptBody}}, {{footer}}, {{poweredByUrl}}. {{receiptBody}} is the legal zone
// built by the POS and must stay in the shell unchanged.
export const DEFAULT_POS_RECEIPT_SHELL = `<!DOCTYPE html>
<html lang="{{lang}}">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<meta name="color-scheme" content="light">
<title>{{heading}}</title>
</head>
<body style="margin:0;padding:0;background:#f3f5f8;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f3f5f8;">
<tr><td align="center" style="padding:24px 12px;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:560px;">
    <tr><td style="padding:0 4px 12px 4px;">
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
        <tr>
          <td align="left" valign="middle"><img src="{{logoUrl}}" alt="NuagePOS" height="32" style="display:block;height:32px;border:0;"></td>
          <td align="right" valign="middle" style="font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#6b7280;">
            Propulsé par <a href="{{poweredByUrl}}" style="color:{{brandColor}};text-decoration:none;font-weight:bold;">NuagePOS</a>
          </td>
        </tr>
      </table>
    </td></tr>
    <tr><td style="background:#ffffff;border-radius:12px;border-top:4px solid {{brandColor}};padding:24px 24px 8px 24px;font-family:Arial,Helvetica,sans-serif;">
      <div style="font-size:20px;font-weight:bold;color:#111827;">{{heading}}</div>
      <div style="font-size:14px;color:#6b7280;padding-top:4px;">{{storeName}}</div>
    </td></tr>
    <tr><td style="background:#ffffff;padding:8px 24px 24px 24px;border-radius:0 0 12px 12px;">
      {{receiptBody}}
    </td></tr>
    <tr><td style="padding:16px 8px;font-family:Arial,Helvetica,sans-serif;font-size:12px;line-height:18px;color:#6b7280;text-align:center;">
      {{footer}}<br>
      <a href="{{poweredByUrl}}" style="color:#6b7280;">nuagepos.com</a>
    </td></tr>
  </table>
</td></tr>
</table>
</body>
</html>`;

export const DEFAULT_POS_RECEIPT_TEMPLATE: PosReceiptEmailTemplate = {
  logoUrl: "https://pos.nuagepos.com/logo.png",
  brandColor: "#0ea5e9",
  headingEn: "Receipt",
  headingFr: "Reçu",
  footerEn: "This electronic receipt was issued by the merchant's point-of-sale system.",
  footerFr: "Ce reçu électronique a été émis par le système de point de vente du commerçant.",
  subjectFormat: "{{receiptLabel}} - {{storeName}} - #{{receiptNumber}} - NuagePOS",
  htmlShell: DEFAULT_POS_RECEIPT_SHELL,
};

export const SAMPLE_RECEIPT_BODY = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="font-family:'Courier New',Courier,monospace;font-size:13px;color:#111827;border:1px solid #111827;">
<tr><td style="padding:16px;">
  <div style="text-align:center;font-weight:bold;font-size:15px;">Entreprise ABC</div>
  <div style="text-align:center;">3800, rue de Marly, Québec QC G1X 4A5</div>
  <div style="text-align:center;padding-top:6px;">Courriel : client@exemple.com</div>
  <div style="border-top:1px dashed #111827;margin:10px 0;"></div>
  <div>Facture : 000123 &nbsp; 2026-09-28 14:32</div>
  <div style="border-top:1px dashed #111827;margin:10px 0;"></div>
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="font-family:inherit;font-size:13px;">
    <tr><td>1 Café</td><td align="right">3,50 $</td></tr>
    <tr><td>2 Croissant</td><td align="right">7,00 $</td></tr>
    <tr><td style="padding-top:6px;">Sous-total</td><td align="right" style="padding-top:6px;">10,50 $</td></tr>
    <tr><td>TPS</td><td align="right">0,53 $</td></tr>
    <tr><td>TVQ</td><td align="right">1,05 $</td></tr>
    <tr><td style="font-weight:bold;">TOTAL</td><td align="right" style="font-weight:bold;">12,08 $</td></tr>
  </table>
  <div style="text-align:center;font-weight:bold;padding-top:10px;">Copie électronique</div>
  <div style="text-align:center;padding-top:10px;"><div style="display:inline-block;width:140px;height:140px;background:#e5e7eb;line-height:140px;">QR</div></div>
  <div style="text-align:center;padding-top:6px;"><a href="https://nuagepos.com" style="color:#111827;">Consulter la transaction en ligne</a></div>
</td></tr>
</table>`;

export function fillPosReceiptShell(template: PosReceiptEmailTemplate, lang: "en" | "fr", receiptBody: string, storeName: string): string {
  const values: Record<string, string> = {
    lang,
    brandColor: template.brandColor || DEFAULT_POS_RECEIPT_TEMPLATE.brandColor,
    logoUrl: template.logoUrl || DEFAULT_POS_RECEIPT_TEMPLATE.logoUrl,
    heading: lang === "fr" ? template.headingFr : template.headingEn,
    footer: lang === "fr" ? template.footerFr : template.footerEn,
    storeName,
    poweredByUrl: "https://nuagepos.com",
    receiptBody,
  };
  return (template.htmlShell || DEFAULT_POS_RECEIPT_SHELL).replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match
  );
}
